/* eslint-disable no-empty */
import {
  BasketballEventReportPossibleGame,
  EventReport,
  RequestBasePostEventReport,
  RequestBasketballGenerateGameReport,
  RequestBasketballGeneratePreGameReport,
  ResponseBasketballGenerateGameReport,
  ResponseBasketballGetPressCenter,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

export const eventReportsApi = createApi({
  reducerPath: 'eventReportsApi',
  baseQuery: API.baseQuery,
  endpoints: (builder) => ({
    fetchPressCenter: builder.query<ResponseBasketballGetPressCenter, number>({
      query: (request) => `basketball/press-center?page=${request || 1}`,
      serializeQueryArgs: ({ endpointName }) => endpointName,
      merge: (currentCache, newItems) => {
        if (currentCache?.data) {
          if (newItems?.current_page === 1) {
            return newItems;
          } else {
            return {
              ...currentCache,
              ...newItems,
              data: [...currentCache.data, ...newItems.data],
            };
          }
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    fetchReport: builder.query<EventReport, number>({
      query: (request) => `event-reports/${request}`,
    }),
    fetchPossibleBasketballGames: builder.query<
      { date: string; games: BasketballEventReportPossibleGame[] }[],
      void
    >({
      query: () => 'basketball/game-reports/possible-games',
    }),
    fetchReportGameData: builder.query<
      BasketballEventReportPossibleGame,
      number
    >({
      query: (request) => `basketball/game-reports/${request}/game`,
    }),
    createReport: builder.mutation<EventReport, RequestBasePostEventReport>({
      query: (request) => ({
        url: `event-reports`,
        method: 'POST',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            eventReportsApi.util.updateQueryData(
              'fetchReport',
              responseData.id,
              (_) => responseData
            )
          );
        } catch (error) {}
      },
    }),
    updateReport: builder.mutation<EventReport, RequestBasePostEventReport>({
      query: (request) => ({
        url: `event-reports/${request.id}`,
        method: 'PUT',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            eventReportsApi.util.updateQueryData(
              'fetchReport',
              responseData.id,
              (_) => responseData
            )
          );
        } catch (error) {}
      },
    }),
    // delete Report and reutrn the id of the deleted report
    deleteReport: builder.mutation<number, number>({
      query: (request) => ({
        url: `event-reports/${request}`,
        method: 'DELETE',
      }),
      transformResponse: (response, meta, arg) => arg,
      async onQueryStarted(request, { dispatch }) {
        try {
          await dispatch(
            eventReportsApi.util.updateQueryData(
              'fetchPressCenter',
              1,
              (draft) => {
                draft.data = draft.data.filter(
                  (report) => report.id !== request
                );
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    generateBasketballGameReport: builder.mutation<
      ResponseBasketballGenerateGameReport,
      RequestBasketballGenerateGameReport
    >({
      query: (request) => ({
        url: `basketball/games/${request.game_id}/generate-report`,
        method: 'POST',
        body: request,
      }),
    }),
    generateBasketballPreGameReport: builder.mutation<
      ResponseBasketballGenerateGameReport,
      RequestBasketballGeneratePreGameReport
    >({
      query: (request) => ({
        url: `basketball/games/${request.game_id}/generate-pregame-report`,
        method: 'POST',
        body: request,
      }),
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  reducerPath,
  reducer,
  middleware,
  useFetchReportQuery,
  useLazyFetchPressCenterQuery,
  useFetchPossibleBasketballGamesQuery,
  useLazyFetchPossibleBasketballGamesQuery,
  useLazyFetchReportGameDataQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
  useGenerateBasketballGameReportMutation,
  useGenerateBasketballPreGameReportMutation,
} = eventReportsApi;
